import { combineUrl } from '../utils/utils';
import { config } from '../config';
import { HttpStatusCodeError } from '../errors/http-status-code-error';
import { InvalidVaultError } from '../errors/invalid-vault-error';
import { InvalidClientDateError } from '../errors/invalid-client-date-error';
import { ErrorCode } from '../dtos/ErrorCode';
export class DefaultHttpClient {
    constructor(baseUrl, uiFrameAdapter, sessionId, logger) {
        this.baseUrl = baseUrl;
        this.uiFrameAdapter = uiFrameAdapter;
        this.sessionId = sessionId;
        this.logger = logger;
    }
    async get(url) {
        const requestOptions = {
            method: 'GET',
            headers: await this.getRequestHeaders()
        };
        return await this.send(url, requestOptions);
    }
    async post(url, body, httpClientOptions) {
        const requestOptions = {
            method: 'POST',
            body: body ? JSON.stringify(body) : null,
            headers: await this.getRequestHeaders()
        };
        return await this.send(url, requestOptions, httpClientOptions);
    }
    async delete(url) {
        const requestOptions = {
            method: 'DELETE',
            headers: await this.getRequestHeaders()
        };
        await this.send(url, requestOptions);
    }
    async send(relativeUri, requestOptions, httpClientOptions) {
        var _a, _b;
        let response;
        let lastError;
        let tries = 0;
        const retryIntervals = (_a = httpClientOptions === null || httpClientOptions === void 0 ? void 0 : httpClientOptions.retryIntervals) !== null && _a !== void 0 ? _a : DefaultHttpClient.retryIntervals;
        for (const retryInterval of retryIntervals) {
            response = null;
            if (tries !== 0) {
                if (httpClientOptions === null || httpClientOptions === void 0 ? void 0 : httpClientOptions.onBeforeRetry) {
                    await (httpClientOptions === null || httpClientOptions === void 0 ? void 0 : httpClientOptions.onBeforeRetry());
                }
            }
            try {
                tries++;
                response = await fetch(combineUrl(this.baseUrl, relativeUri), requestOptions);
                // retry if neccessary
                if (tries < retryIntervals.length && DefaultHttpClient.shouldRetry(response)) {
                    this.logger.logWarning(`HTTP: Request failed with response status code '${response.status}'.`);
                    this.logger.logInformation(`HTTP: Retrying in '${retryInterval}' milliseconds...`);
                    await DefaultHttpClient.delay(retryInterval);
                    continue;
                }
            }
            catch (error) {
                lastError = error;
                if (tries <= retryIntervals.length) {
                    this.logger.logWarning(`HTTP: ${(_b = error) !== null && _b !== void 0 ? _b : 'Request failed with error.'}`);
                    this.logger.logInformation(`HTTP: Retrying in '${retryInterval}' milliseconds...`);
                    await DefaultHttpClient.delay(retryInterval);
                    continue;
                }
            }
            if (response) {
                if (response.status === 509) {
                    this.uiFrameAdapter.navigateToApp('/zero-trust', '/invalid-vault');
                    throw new InvalidVaultError('The Vault is invalid');
                }
                if (response.status >= 400 || response.status === 207) {
                    let data;
                    try {
                        data = await this.handleResponse(response);
                    }
                    catch (_c) {
                        throw new HttpStatusCodeError(response.status, response.statusText);
                    }
                    if (data.errorCode === ErrorCode.InvalidClientDate) {
                        this.uiFrameAdapter.addToast('Time is set incorrectly on this device. Make sure it has the right time and try again.', 'error');
                        throw new InvalidClientDateError(data.message);
                    }
                    throw new HttpStatusCodeError(response.status, response.statusText);
                }
                // deserialize
                const data = await this.handleResponse(response);
                return data;
            }
        }
        this.logger.logInformation('HTTP: All retries have failed.');
        this.logger.logError(`HTTP: ${lastError}`);
        throw lastError;
    }
    async getRequestHeaders() {
        const requestHeaders = new Headers();
        requestHeaders.set('Content-Type', 'application/json');
        requestHeaders.set('X-Client-Version', `JavaScript/${config.version}`);
        requestHeaders.set('X-Client-Component', this.uiFrameAdapter.clientComponent);
        requestHeaders.set('traceparent', this.sessionId);
        const token = await this.uiFrameAdapter.accessTokenFactory();
        requestHeaders.set('Authorization', `Bearer ${token}`);
        return requestHeaders;
    }
    async handleResponse(response) {
        const json = await response.text();
        let data;
        try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            data = json && JSON.parse(json);
        }
        catch (error) {
            this.logger.logError(`HTTP: Response can't be parsed as json.`);
            throw error;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return data;
    }
    static delay(milliseconds) {
        return new Promise((resolve) => {
            setTimeout(() => resolve(), milliseconds);
        });
    }
    static shouldRetry(response) {
        return response.status === 0 || response.status === 408 || response.status === 429 || (response.status >= 500 && response.status !== 509);
    }
}
DefaultHttpClient.retryIntervals = [0, 1000, 3000, 5000, 25000];
